import React, { useMemo } from 'react';
import { useLocation, useNavigate } from 'react-router';

import { useShallow } from 'zustand/react/shallow';

import {
  CalendarIcon,
  CloseIcon,
  DescriptionIcon,
  Drawer,
  LogoutIcon,
  MedicalIcon,
  SavingsIcon,
  SettingsIcon,
  Text,
  TypeMenuState,
  dialogService,
} from '@thefarmersfront/kitchen-mobile';
import clsx from 'clsx';

import {
  CONTRACT_TYPE,
  DATE_FORMAT_TO_MINUTE,
  DIALOG_MESSAGES,
  DIALOG_TITLES,
  ENVIRONMENT,
  SUBMISSION_STATUS_CODE,
  VERSION,
} from '../../constants';
import { useGetLatestUserAndCommuteInfo, useLeaveWork, useLogout, useValidNetwork } from '../../hooks';
import {
  useContractInputStore,
  useShouldCheckModalStore,
  useTabInfoStore,
  useTokenStore,
  useUserInfoStore,
  useWorkInfoStore,
} from '../../store';
import { getFormattedDate, getWorkingTimeText } from '~/utils';

import SpecialMedicalBadge from '../SubmitSpecialMedical/SpecialMedicalBadge';
import MenuDrawerBanner from './MenuDrawerBanner';

import styles from './MenuDrawer.module.scss';

/**
 * MenuDrawer 컴포넌트 props type 정의
 * @category MenuDrawer
 */
export type TypeMenuDrawerProps = {
  /** 앱 버전 */
  appVersion: string | null;
  /** 드로어의 표시 여부 */
  isDrawerOpen: boolean;
  /** 드로어를 닫는 이벤트 */
  onClose: () => void;
};

/**
 * @description MenuDrawer 컴포넌트
 * @param TypeMenuDrawerProps
 * @returns - React.JSX.Element / MenuDrawer
 */
const MenuDrawer = ({ appVersion, isDrawerOpen, onClose }: TypeMenuDrawerProps): React.JSX.Element => {
  const navigate = useNavigate();
  const { pathname } = useLocation();

  const { specialMedicalExamination, contractType } = useUserInfoStore(
    useShallow((state) => ({
      specialMedicalExamination: state.specialMedicalExamination,
      contractType: state.contractType,
    }))
  );
  const { resetUserInfo, laborAccountId } = useUserInfoStore(
    useShallow((state) => ({
      resetUserInfo: state.actions.resetUserInfo,
      laborAccountId: state.laborAccountId,
    }))
  );
  const { isCommute, resetWorkInfo } = useWorkInfoStore(
    useShallow((state) => ({
      isCommute: state.actions.getIsCommute(),
      resetWorkInfo: state.actions.resetWorkInfo,
    }))
  );
  const { resetTokens } = useTokenStore((state) => state.actions);
  const { resetContractInputState } = useContractInputStore((state) => state.actions);
  const { resetTabInfo } = useTabInfoStore((state) => state.actions);
  const { resetChangePasswordModal } = useShouldCheckModalStore((state) => state.actions);

  /** 검진 확인서 제출 메뉴 숨김 여부 */
  const hiddenTargetSubmitSpecialMedical = useMemo(
    () =>
      !specialMedicalExamination ||
      specialMedicalExamination?.submissionStatus?.code === SUBMISSION_STATUS_CODE.UNAVAILABLE,
    [specialMedicalExamination]
  );

  const requestLogout = useLogout();
  const requestLeaveWork = useLeaveWork();
  const { checkValidNetwork } = useValidNetwork();
  const getLatestUserAndCommuteInfo = useGetLatestUserAndCommuteInfo();

  const handleLogoutClick = (): void => {
    dialogService.addDialog({
      children: DIALOG_TITLES.ASK_LOGOUT,
      type: 'confirm',
      confirmText: '확인',
      onConfirm: handleConfirmLogout,
    });
  };

  const handleConfirmLogout = (): void => {
    onClose();

    requestLogout({
      onSuccess: (response) => {
        if (response) {
          dialogService.addDialog({
            children: DIALOG_MESSAGES.COMPLETED_LOGOUT,
          });

          resetUserInfo();
          resetWorkInfo();
          resetTokens();
          resetContractInputState();
          resetTabInfo();
          resetChangePasswordModal();
          navigate('/login', { replace: true });
        }
      },
    });
  };

  const handleLeaveWork = async () => {
    const isValidNetwork = await checkValidNetwork();

    if (!isValidNetwork) {
      return;
    }

    dialogService.addDialog({
      title: DIALOG_TITLES.ASK_LEAVE_WORK,
      children: <Text color="secondary">{DIALOG_MESSAGES.ASK_LEAVE_WORK}</Text>,
      type: 'confirm',
      confirmText: '퇴근',
      onConfirm: handleConfirmLeaveWork,
    });
  };

  const handleConfirmLeaveWork = (): void => {
    requestLeaveWork({
      params: {
        laborAccountId,
      },
      onSuccess: (response) => {
        if (response) {
          dialogService.addDialog({
            title: DIALOG_TITLES.COMPLETED_LEAVE_WORK,
            children: (
              <div className={styles['leave-work-result-container']}>
                <div className={styles['leave-work-time-container']}>
                  <Text color="secondary">퇴근 시각</Text>
                  <Text color="secondary">
                    {getFormattedDate({ date: response?.endWorkTime, format: DATE_FORMAT_TO_MINUTE, separator: '.' })}
                  </Text>
                </div>
                <div className={styles['leave-work-time-container']}>
                  <Text color="secondary">총 근무 시간</Text>
                  <Text color="secondary">{getWorkingTimeText(response?.workingHour, response?.workingMinute)}</Text>
                </div>
              </div>
            ),
            onConfirm: () => {
              getLatestUserAndCommuteInfo();
            },
          });
        }
      },
    });
  };

  const environmentText = useMemo(() => {
    if (ENVIRONMENT === 'staging') {
      return 'STG';
    }

    if (ENVIRONMENT === 'development') {
      return 'DEV';
    }

    if (ENVIRONMENT === 'qa') {
      return 'QA';
    }

    return '';
  }, [ENVIRONMENT]);

  const menus = useMemo<TypeMenuState[]>(() => {
    return [
      {
        label: '나의 근무',
        key: '/monthly-timeline',
        type: 'list',
        leftIcon: <CalendarIcon size={16} />,
        hasDivider: contractType === CONTRACT_TYPE.CONTRACT && hiddenTargetSubmitSpecialMedical,
      },
      {
        label: '계약서 관리',
        key: '/contract-management',
        hidden: contractType !== CONTRACT_TYPE.SHORT,
        leftIcon: <DescriptionIcon size={16} />,
        type: 'list',
      },
      {
        label: '급여명세서',
        key: '/pay-statement/check-password',
        hidden: contractType !== CONTRACT_TYPE.SHORT,
        leftIcon: <SavingsIcon size={16} />,
        type: 'list',
        hasDivider: contractType === CONTRACT_TYPE.SHORT && hiddenTargetSubmitSpecialMedical,
      },
      {
        label: '검진 확인서 제출',
        key: '/submit-special-medical',
        hidden: hiddenTargetSubmitSpecialMedical,
        leftIcon: <MedicalIcon size={16} />,
        rightField: <SpecialMedicalBadge />,
        type: 'list',
        hasDivider: !hiddenTargetSubmitSpecialMedical,
      },
      {
        label: '설정',
        key: '/settings',
        leftIcon: <SettingsIcon size={16} />,
        type: 'list',
      },
      {
        label: '퇴근',
        key: '/leave-work',
        leftIcon: <CloseIcon size={16} />,
        hidden: !(contractType === CONTRACT_TYPE.CONTRACT && isCommute),
        type: 'list',
      },
      {
        label: '로그아웃',
        key: '/logout',
        leftIcon: <LogoutIcon size={16} />,
        type: 'list',
      },
      {
        label: `${appVersion ? 'App v.' + appVersion + ' | ' : ''}Web v.${VERSION}`,
        key: 'version',
        type: 'version',
      },
      {
        label: environmentText,
        key: 'environment',
        className: clsx(styles['menu-environment'], ENVIRONMENT && styles[ENVIRONMENT]),
        hidden: ENVIRONMENT === 'production',
        type: 'list',
      },
    ];
  }, [appVersion, contractType, environmentText, isCommute, hiddenTargetSubmitSpecialMedical]);

  const handleMenuClick = (key: string) => {
    const isSamePath = pathname === key || (pathname.includes('/pay-statement') && key.includes('/pay-statement'));

    if (isSamePath) {
      return;
    }

    if (key === '/logout') {
      handleLogoutClick();
      return;
    }

    if (key === '/leave-work') {
      handleLeaveWork();
      return;
    }

    if (key === '/submit-special-medical') {
      const isSecondExamination =
        specialMedicalExamination?.secondExaminationInProgress &&
        specialMedicalExamination?.status?.code.includes('SECOND');
      navigate(key, { state: { type: isSecondExamination ? 'SECOND' : 'FIRST' } });
      return;
    }

    navigate(key);
    onClose();
  };

  return (
    <Drawer
      className={styles['menu-drawer']}
      open={isDrawerOpen}
      customHeaderRenderer={() => <MenuDrawerBanner contractType={contractType} closeDrawer={onClose} />}
      menus={menus}
      onClick={handleMenuClick}
      onClose={onClose}
    />
  );
};

export default MenuDrawer;
